import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Eye, Lock, Database, UserCheck, Globe, Mail, Trash2 } from 'lucide-react';

const PrivacyPage = () => {
  const lastUpdate = "25 février 2026";
  
  return (
    <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary/10 to-background py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Shield className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl font-bold font-serif mb-4">Politique de Confidentialité</h1>
            <p className="text-gray-600">Dernière mise à jour : {lastUpdate}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg p-8 md:p-12"
          >
            <div className="prose prose-lg max-w-none space-y-8">
              
              {/* Introduction */}
              <section>
                <p className="text-gray-700 text-lg">
                  Chez XPerience, nous accordons une importance capitale à la protection de vos données personnelles. 
                  Cette politique de confidentialité explique comment nous collectons, utilisons et protégeons vos informations.
                </p>
                
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 mt-4">
                  <p className="text-blue-800 font-medium">Votre consentement</p>
                  <p className="text-blue-700 text-sm mt-1">
                    En créant un compte sur XPerience, vous consentez expressément à la collecte et au traitement de vos données personnelles conformément à la présente politique. 
                    Ce consentement est matérialisé par le cochage de la case "J'accepte la politique de confidentialité" lors de l'inscription.
                  </p>
                </div>
              </section>

              {/* Article 1 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <UserCheck className="w-6 h-6 text-primary" />
                  1. Responsable du traitement
                </h2>
                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                  <p className="text-gray-700"><strong>XPerience</strong></p>
                  <p className="text-gray-700">Fribourg, Suisse</p>
                  <p className="text-gray-700">Email : contact@xperience-guides.com</p>
                </div>
              </section>

              {/* Article 2 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <Database className="w-6 h-6 text-primary" />
                  2. Données collectées
                </h2>
                
                <h3 className="text-lg font-semibold mt-4">2.1 Données fournies par l'utilisateur</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Inscription</strong> : nom, prénom, adresse email, mot de passe</li>
                  <li><strong>Profil Guide</strong> : photo, biographie, localisation, langues parlées, spécialités</li>
                  <li><strong>Réservation</strong> : dates, nombre de participants, messages</li>
                  <li><strong>Paiement</strong> : informations traitées par notre prestataire Stripe (nous ne stockons pas vos données bancaires)</li>
                </ul>
                
                <h3 className="text-lg font-semibold mt-4">2.2 Données collectées automatiquement</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Adresse IP</li>
                  <li>Type de navigateur et appareil</li>
                  <li>Pages visitées et interactions</li>
                  <li>Données de géolocalisation (si autorisées)</li>
                </ul>
              </section>

              {/* Article 3 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <Eye className="w-6 h-6 text-primary" />
                  3. Utilisation des données
                </h2>
                <p className="text-gray-700">Nous utilisons vos données pour :</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Fournir et améliorer nos services</li>
                  <li>Permettre la mise en relation entre Guides et Voyageurs</li>
                  <li>Traiter les paiements et les remboursements</li>
                  <li>Envoyer des communications relatives à vos réservations</li>
                  <li>Assurer la sécurité de la plateforme</li>
                  <li>Résoudre les litiges</li>
                  <li>Respecter nos obligations légales</li>
                </ul>
              </section>

              {/* Article 4 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <Globe className="w-6 h-6 text-primary" />
                  4. Partage des données
                </h2>
                <p className="text-gray-700">Vos données peuvent être partagées avec :</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Autres utilisateurs</strong> : informations de profil public (nom, photo, expériences)</li>
                  <li><strong>Prestataires de services</strong> : 
                    <ul className="list-circle pl-6 mt-2">
                      <li>Stripe (paiements)</li>
                      <li>Hébergeur (stockage des données)</li>
                    </ul>
                  </li>
                  <li><strong>Autorités</strong> : si requis par la loi</li>
                </ul>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500 mt-4">
                  <p className="text-green-800">
                    <strong>Nous ne vendons jamais vos données personnelles à des tiers.</strong>
                  </p>
                </div>
              </section>

              {/* Article 5 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <Lock className="w-6 h-6 text-primary" />
                  5. Sécurité des données
                </h2>
                <p className="text-gray-700">Nous mettons en œuvre des mesures de sécurité appropriées :</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Chiffrement SSL/TLS pour toutes les communications</li>
                  <li>Stockage sécurisé des mots de passe (hachage)</li>
                  <li>Accès restreint aux données personnelles</li>
                  <li>Surveillance continue des systèmes</li>
                </ul>
              </section>

              {/* Article 6 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
                  6. Conservation des données
                </h2>
                <p className="text-gray-700">
                  Nous conservons vos données personnelles aussi longtemps que nécessaire pour fournir nos services et respecter nos obligations légales :
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Compte actif</strong> : pendant toute la durée de votre inscription</li>
                  <li><strong>Après suppression du compte</strong> : 3 ans pour les données de transaction (obligations légales)</li>
                  <li><strong>Données de connexion</strong> : 1 an</li>
                </ul>
              </section>

              {/* Article 7 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
                  7. Vos droits
                </h2>
                <p className="text-gray-700">Conformément à la LPD suisse et au RGPD, vous disposez des droits suivants :</p>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  {[
                    { title: "Droit d'accès", desc: "Obtenir une copie de vos données" },
                    { title: "Droit de rectification", desc: "Corriger vos données inexactes" },
                    { title: "Droit à l'effacement", desc: "Demander la suppression de vos données" },
                    { title: "Droit à la portabilité", desc: "Recevoir vos données dans un format standard" },
                    { title: "Droit d'opposition", desc: "Vous opposer à certains traitements" },
                    { title: "Droit de retrait", desc: "Retirer votre consentement à tout moment" }
                  ].map((right, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-semibold text-gray-800">{right.title}</p>
                      <p className="text-gray-600 text-sm">{right.desc}</p>
                    </div>
                  ))}
                </div>
                
                {/* Contact box - highly visible */}
                <div className="bg-primary/10 border-2 border-primary rounded-lg p-6 mt-6">
                  <p className="font-bold text-gray-800 text-lg mb-2">📧 Pour exercer vos droits :</p>
                  <p className="text-gray-700">
                    Envoyez un email à <strong className="text-primary">contact@xperience-guides.com</strong>
                  </p>
                  <p className="text-gray-600 text-sm mt-2">
                    Précisez le droit que vous souhaitez exercer et joignez une copie de votre pièce d'identité. 
                    Nous répondons sous 30 jours maximum.
                  </p>
                </div>
              </section>

              {/* Article 8 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
                  8. Cookies
                </h2>
                <p className="text-gray-700">
                  Nous utilisons des cookies essentiels au fonctionnement du site :
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Cookies de session</strong> : maintien de votre connexion</li>
                  <li><strong>Cookies de préférence</strong> : langue, paramètres d'affichage</li>
                </ul>
                <p className="text-gray-700 mt-4">
                  Vous pouvez configurer votre navigateur pour refuser les cookies, mais certaines fonctionnalités pourraient ne pas fonctionner correctement.
                </p>
              </section>

              {/* Article 9 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
                  9. Transferts internationaux
                </h2>
                <p className="text-gray-700">
                  Vos données peuvent être transférées vers des serveurs situés en dehors de la Suisse. 
                  Dans ce cas, nous nous assurons que des garanties appropriées sont en place (clauses contractuelles types, décisions d'adéquation).
                </p>
              </section>

              {/* Article 10 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
                  10. Modifications
                </h2>
                <p className="text-gray-700">
                  Nous pouvons mettre à jour cette politique de confidentialité. 
                  En cas de modification substantielle, nous vous en informerons par email ou via la plateforme.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <Mail className="w-6 h-6 text-primary" />
                  11. Contact
                </h2>
                <p className="text-gray-700">
                  Pour toute question concernant cette politique ou vos données personnelles :
                </p>
                <div className="bg-primary/5 p-6 rounded-lg mt-4">
                  <p className="text-gray-800"><strong>XPerience - Protection des données</strong></p>
                  <p className="text-gray-700">Email : contact@xperience-guides.com</p>
                  <p className="text-gray-700">Adresse : Fribourg, Suisse</p>
                </div>
                <p className="text-gray-700 mt-4">
                  Vous avez également le droit d'introduire une réclamation auprès du <strong>Préposé fédéral à la protection des données et à la transparence (PFPDT)</strong> en Suisse.
                </p>
              </section>

            </div>

            {/* Footer */}
            <div className="mt-12 pt-8 border-t border-gray-200 text-center">
              <p className="text-gray-500 text-sm">
                © 2026 XPerience - Tous droits réservés
              </p>
              <p className="text-gray-400 text-xs mt-2">
                Dernière mise à jour : {lastUpdate}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPage;
